/**
 * FinanceBlock Component - 財務展示區塊
 * Requirements: 7.4
 * 
 * 剩餘天數和進度條、售價顯示（目標幣種）、溢價/折價標籤、續費價格資訊
 */

import type { CardData, CalculationResult } from '../../types/card';
import type { Translations } from '../../i18n';

interface FinanceBlockProps {
  cardData: CardData;
  result: CalculationResult;
  t: Translations;
}

/** 格式化價格 */
function formatPrice(price: number, currency: string): string {
  return `${currency} ${price.toFixed(2)}`;
}

/** 取得進度條顏色 */
function getProgressColor(progress: number): string {
  if (progress > 60) return 'bg-green-500';
  if (progress > 30) return 'bg-yellow-500';
  return 'bg-red-500';
}

/** 取得計費週期標籤 */
function getBillingCycleLabel(billingCycle: number, t: Translations): string {
  const labels: Record<number, string> = {
    0: t.lifetime,
    30: t.monthly,
    365: t.yearly,
    730: t.twoYears,
  };
  return labels[billingCycle] || `${billingCycle}${t.days}`;
}

export function FinanceBlock({ cardData, result, t }: FinanceBlockProps) {
  const { currency, renewalPrice, billingCycle, targetCurrency } = cardData;
  const { remainingDays, residualValue, residualValueTarget, sellingPriceTarget, premium, isPremium, progress } = result;
  const isLifetime = billingCycle === 0;

  return (
    <div className="p-4 border-t border-slate-200">
      <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-3">{t.priceInfo}</h3>

      {/* 剩餘天數和進度條 */}
      <div className="mb-4">
        <div className="flex justify-between items-center mb-1">
          <span className="text-sm text-slate-600">{t.remainingTime}</span>
          <span className="font-semibold text-slate-800">
            {isLifetime ? t.lifetimeValid : `${remainingDays} ${t.days}`}
          </span>
        </div>
        {!isLifetime && (
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div
              className={`${getProgressColor(progress)} h-2 rounded-full transition-all duration-300`}
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
        )}
      </div>

      {/* 價格資訊網格 */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {/* 續費價格 */}
        <div className="bg-slate-50 rounded-lg p-3">
          <p className="text-xs text-slate-500 mb-1">{t.renewalPrice} ({getBillingCycleLabel(billingCycle, t)})</p>
          <p className="font-semibold text-slate-700">{formatPrice(renewalPrice, currency)}</p>
        </div>

        {/* 剩餘價值 (非終身) */}
        {!isLifetime && (
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-500 mb-1">{t.residualValue}</p>
            <p className="font-semibold text-slate-700">{targetCurrency} {residualValueTarget.toFixed(0)}</p>
            <p className="text-xs text-slate-400">≈ {formatPrice(residualValue, currency)}</p>
          </div>
        )}
      </div>

      {/* 售價區塊 */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-blue-100 text-sm mb-1">{t.price}</p>
            <p className="text-2xl font-bold">{targetCurrency} {sellingPriceTarget}</p>
          </div>
          
          {/* 溢價/折價標籤 (非終身) */}
          {!isLifetime && (
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${
              isPremium 
                ? 'bg-red-400/20 text-red-100' 
                : 'bg-green-400/20 text-green-100'
            }`}>
              {isPremium ? t.premium : t.discount} {targetCurrency} {premium.toFixed(0)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FinanceBlock;
